import { create } from "zustand";
import { persist } from "zustand/middleware";
import { fetchBrands } from "./brandsApi";

/**
 * Brand Context Store
 * Manages the currently selected brand and provides brand-switching functionality
 */
export const useBrandContext = create(
  persist(
    (set, get) => ({
      // Currently selected brand
      selectedBrand: null,
      
      // All brands available to the user
      brands: [],
      
      // Loading and error states
      isLoading: false,
      error: null,

      /**
       * Set the currently selected brand
       */
      setSelectedBrand: (brand) => {
        set({ selectedBrand: brand });
        
        // Update URL when brand changes (handled by component)
        if (brand) {
          localStorage.setItem("lastSelectedBrandId", brand.id);
        }
      },

      /**
       * Fetch all brands for the current user
       */
      fetchBrands: async () => {
        set({ isLoading: true, error: null });
        try {
          const brands = await fetchBrands();
          set({ brands, isLoading: false });
          
          // Auto-select a brand if none is selected, or if the selected brand no longer exists
          const { selectedBrand } = get();
          const stillExists = selectedBrand && brands.find(b => b.id === selectedBrand.id);
          if (!stillExists && brands.length > 0) {
            // Try to restore last selected brand
            const lastBrandId = localStorage.getItem("lastSelectedBrandId");
            const lastBrand = brands.find(b => b.id === lastBrandId);

            if (lastBrand) {
              set({ selectedBrand: lastBrand });
            } else {
              // Default to first brand
              set({ selectedBrand: brands[0] });
            }
          } else if (!stillExists && brands.length === 0) {
            set({ selectedBrand: null });
            localStorage.removeItem("lastSelectedBrandId");
          }
          
          return brands;
        } catch (error) {
          console.error("Error fetching brands:", error);
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      /**
       * Select brand by ID
       */
      selectBrandById: (brandId) => {
        const { brands } = get();
        const brand = brands.find(b => b.id === brandId);
        if (brand) {
          set({ selectedBrand: brand });
          localStorage.setItem("lastSelectedBrandId", brandId);
        }
      },

      /**
       * Refresh the selected brand data
       */
      refreshSelectedBrand: async () => {
        const { selectedBrand, brands } = get();
        if (!selectedBrand) return;
        
        // Refetch all brands to get updated data
        await get().fetchBrands();
        
        // Re-select the same brand with updated data
        const updatedBrand = get().brands.find(b => b.id === selectedBrand.id);
        if (updatedBrand) {
          set({ selectedBrand: updatedBrand });
        }
      },

      /**
       * Clear selected brand (useful for logout)
       */
      clearSelectedBrand: () => {
        set({ selectedBrand: null, brands: [] });
        localStorage.removeItem("lastSelectedBrandId");
      },
    }),
    {
      name: "brand-context-storage",
      partialize: (state) => ({
        // Only persist selectedBrand ID, not the full object
        selectedBrandId: state.selectedBrand?.id || null,
      }),
    }
  )
);

/**
 * Hook to get the current brand ID (useful for URL generation)
 */
export const useCurrentBrandId = () => {
  return useBrandContext((state) => state.selectedBrand?.id);
};

/**
 * Hook to check if a brand is currently selected
 */
export const useHasSelectedBrand = () => {
  return useBrandContext((state) => !!state.selectedBrand);
};
